// src/pages/ConversationViewerPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { useLanguage } from '../context/LanguageContext.js';
import { useConversations } from '../hooks/useConversations.js';
import './ConversationViewerPage.css';

const translations = {
  en: {
    title: 'WhatsApp Conversations',
    subtitle: 'Review and approve for AI training',
    filters: 'Filters',
    all: 'All',
    pending: 'Pending',
    approved: 'Approved',
    search: 'Search by name or phone...',
    noConversations: 'No conversations found',
    noConversationsDesc: 'Conversations will appear here as you interact with clients via WhatsApp',
    selectConversation: 'Select a conversation',
    selectConversationDesc: 'Choose a conversation from the list to view details',
    messages: 'messages',
    approve: 'Approve for Training',
    removeApproval: 'Remove Approval',
    approvedBadge: 'Approved',
    closed: 'Closed',
    active: 'Active',
    incoming: 'Client',
    outgoing: 'You',
    approveSuccess: 'Conversation approved for training!',
    removeSuccess: 'Approval removed!',
    accessDenied: 'Access Denied - Admin only',
    trainAI: 'Train AI',
    trainingNotes: 'Add notes for training (optional)...',
    notesPlaceholder: 'Example: Good example of scheduling confirmation, client satisfied with service...',
    totalConversations: 'Total',
    approvedCount: 'Approved',
    pendingCount: 'Pending',
    today: 'Today',
    yesterday: 'Yesterday'
  },
  pt: {
    title: 'Conversas WhatsApp',
    subtitle: 'Revise e aprove para treinamento IA',
    filters: 'Filtros',
    all: 'Todas',
    pending: 'Pendentes',
    approved: 'Aprovadas',
    search: 'Buscar por nome ou telefone...',
    noConversations: 'Nenhuma conversa encontrada',
    noConversationsDesc: 'Conversas aparecerão aqui conforme você interage com clientes via WhatsApp',
    selectConversation: 'Selecione uma conversa',
    selectConversationDesc: 'Escolha uma conversa da lista para ver detalhes',
    messages: 'mensagens',
    approve: 'Aprovar para Treinamento',
    removeApproval: 'Remover Aprovação',
    approvedBadge: 'Aprovada',
    closed: 'Fechada',
    active: 'Ativa',
    incoming: 'Cliente',
    outgoing: 'Você',
    approveSuccess: 'Conversa aprovada para treinamento!',
    removeSuccess: 'Aprovação removida!',
    accessDenied: 'Acesso Negado - Apenas Admin',
    trainAI: 'Treinar IA',
    trainingNotes: 'Adicionar notas para treinamento (opcional)...',
    notesPlaceholder: 'Exemplo: Bom exemplo de confirmação de agendamento, cliente satisfeito com serviço...',
    totalConversations: 'Total',
    approvedCount: 'Aprovadas',
    pendingCount: 'Pendentes',
    today: 'Hoje',
    yesterday: 'Ontem'
  }
};

// Gera cor baseada no nome
const getAvatarColor = (name) => {
  if (!name) return '#6c757d';
  const colors = [
    '#2b8ac4', '#3a9dd4', '#4baee0', '#28a745', '#20c997',
    '#6f42c1', '#e83e8c', '#fd7e14', '#ffc107', '#17a2b8'
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// Formata data relativa
const formatRelativeDate = (dateString, language) => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDate.getTime() === today.getTime()) {
    return translations[language]?.today || 'Today';
  } else if (msgDate.getTime() === yesterday.getTime()) {
    return translations[language]?.yesterday || 'Yesterday';
  } else {
    return date.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-NZ', {
      day: '2-digit',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  }
};

// Formata hora
const formatTime = (dateString, language) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString(language === 'pt' ? 'pt-BR' : 'en-NZ', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Agrupa mensagens por data
const groupMessagesByDate = (messages) => {
  if (!messages || messages.length === 0) return [];

  const groups = [];
  let currentDate = null;
  let currentGroup = null;

  messages.forEach(msg => {
    const msgDate = new Date(msg.timestamp).toDateString();

    if (msgDate !== currentDate) {
      currentDate = msgDate;
      currentGroup = {
        date: msg.timestamp,
        messages: []
      };
      groups.push(currentGroup);
    }

    currentGroup.messages.push(msg);
  });

  return groups;
};

// Agrupa conversas por telefone (para mostrar historico completo)
const groupConversationsByPhone = (conversations) => {
  if (!conversations || conversations.length === 0) return [];

  const grouped = {};

  conversations.forEach(conv => {
    const phone = conv.phone_number;
    if (!grouped[phone]) {
      grouped[phone] = {
        phone_number: phone,
        client_name: conv.client_name,
        conversations: [],
        total_messages: 0,
        latest_date: conv.started_at,
        earliest_date: conv.started_at,
        has_approved: false,
        all_approved: true,
        conversation_ids: []
      };
    }

    grouped[phone].conversations.push(conv);
    grouped[phone].conversation_ids.push(conv.id);
    grouped[phone].total_messages += conv.message_count || 0;

    // Atualizar nome do cliente (pegar o mais recente)
    if (conv.client_name && new Date(conv.started_at) > new Date(grouped[phone].latest_date)) {
      grouped[phone].client_name = conv.client_name;
    }

    // Atualizar datas
    if (new Date(conv.started_at) > new Date(grouped[phone].latest_date)) {
      grouped[phone].latest_date = conv.started_at;
    }
    if (new Date(conv.started_at) < new Date(grouped[phone].earliest_date)) {
      grouped[phone].earliest_date = conv.started_at;
    }

    // Verificar aprovacoes
    if (conv.is_approved_for_training) {
      grouped[phone].has_approved = true;
    } else {
      grouped[phone].all_approved = false;
    }
  });

  // Converter para array e ordenar por data mais recente
  return Object.values(grouped)
    .sort((a, b) => new Date(b.latest_date) - new Date(a.latest_date));
};

export default function ConversationViewerPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const {
    conversations,
    loading,
    error,
    fetchAllConversations,
    getMessagesByConversationIds,
    approveConversation,
    removeApproval
  } = useConversations();

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null); // Grupo de conversas por telefone
  const [allMessages, setAllMessages] = useState([]); // Todas as mensagens do telefone
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [trainingNotes, setTrainingNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const t = translations[language] || translations.en;
  const isAuthorized = user?.isAdmin;

  // Agrupar conversas por telefone
  const groupedConversations = groupConversationsByPhone(conversations);

  // Stats baseados nos grupos
  const totalCount = groupedConversations.length;
  const approvedCount = groupedConversations.filter(g => g.all_approved).length;
  const pendingCount = groupedConversations.filter(g => !g.all_approved).length;

  // Fetch conversations when filters change
  useEffect(() => {
    fetchAllConversations({
      status: statusFilter,
      search: searchQuery
    });
  }, [statusFilter, searchQuery, fetchAllConversations]);

  // Scroll to bottom when messages load
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages]);

  // Select conversation group (all conversations from same phone)
  const handleSelectGroup = async (group) => {
    setSelectedGroup(group);
    setLoadingDetail(true);
    setTrainingNotes('');
    setAllMessages([]);

    try {
      // Buscar mensagens de TODAS as conversas desse telefone
      const messages = await getMessagesByConversationIds(group.conversation_ids);
      setAllMessages(messages);
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Approve all conversations in the group
  const handleApprove = async () => {
    if (!selectedGroup) return;

    setActionLoading(true);
    try {
      // Aprovar TODAS as conversas do grupo
      for (const convId of selectedGroup.conversation_ids) {
        await approveConversation(convId, trainingNotes || null);
      }
      setSuccessMessage(t.approveSuccess);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Update local state
      setSelectedGroup(prev => ({ ...prev, all_approved: true, has_approved: true }));

      // Refresh list
      await fetchAllConversations({ status: statusFilter, search: searchQuery });
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Remove approval from all conversations in the group
  const handleRemoveApproval = async () => {
    if (!selectedGroup) return;

    setActionLoading(true);
    try {
      // Remover aprovacao de TODAS as conversas do grupo
      for (const convId of selectedGroup.conversation_ids) {
        await removeApproval(convId);
      }
      setSuccessMessage(t.removeSuccess);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Update local state
      setSelectedGroup(prev => ({ ...prev, all_approved: false, has_approved: false }));

      // Refresh list
      await fetchAllConversations({ status: statusFilter, search: searchQuery });
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Get preview text for a group (last conversation's last message)
  const getPreviewText = (group) => {
    // Pegar a conversa mais recente do grupo
    const latestConv = group.conversations.sort((a, b) =>
      new Date(b.started_at) - new Date(a.started_at)
    )[0];

    if (latestConv?.last_message_text) {
      return latestConv.last_message_text.length > 50
        ? latestConv.last_message_text.substring(0, 50) + '...'
        : latestConv.last_message_text;
    }
    return `${group.total_messages} ${t.messages}`;
  };

  if (!isAuthorized) {
    return (
      <Container className="py-4">
        <h5>{t.accessDenied}</h5>
      </Container>
    );
  }

  const messageGroups = groupMessagesByDate(allMessages);

  return (
    <Container fluid style={{ padding: '20px 40px' }}>
      {/* Header */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
          <h3 style={{ fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
            <span style={{ marginRight: 8 }}>💬</span>
            {t.title}
          </h3>
          <p style={{ color: '#666', marginBottom: 0 }}>{t.subtitle}</p>
        </div>
        {approvedCount > 0 && (
          <button
            className="cvp-train-btn"
            onClick={() => navigate('/admin/training/sessions')}
          >
            🤖 {t.trainAI} ({approvedCount})
          </button>
        )}
      </div>

      {/* Success Message */}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert variant="danger">Error: {error}</Alert>
      )}

      {/* Main Container - Split View */}
      <div className="cvp-container">
        {/* Sidebar - Lista */}
        <div className="cvp-sidebar">
          {/* Stats */}
          <div className="cvp-stats">
            <div className="cvp-stat-item">
              <div className="cvp-stat-value">{totalCount}</div>
              <div className="cvp-stat-label">{t.totalConversations}</div>
            </div>
            <div className="cvp-stat-item">
              <div className="cvp-stat-value">{approvedCount}</div>
              <div className="cvp-stat-label">{t.approvedCount}</div>
            </div>
            <div className="cvp-stat-item">
              <div className="cvp-stat-value">{pendingCount}</div>
              <div className="cvp-stat-label">{t.pendingCount}</div>
            </div>
          </div>

          {/* Filters */}
          <div className="cvp-filters">
            <input
              type="text"
              className="cvp-search-input"
              placeholder={t.search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="cvp-filter-tabs">
              <button
                className={`cvp-filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => setStatusFilter('all')}
              >
                {t.all}
              </button>
              <button
                className={`cvp-filter-tab ${statusFilter === 'pending_review' ? 'active' : ''}`}
                onClick={() => setStatusFilter('pending_review')}
              >
                {t.pending}
              </button>
              <button
                className={`cvp-filter-tab ${statusFilter === 'approved' ? 'active' : ''}`}
                onClick={() => setStatusFilter('approved')}
              >
                {t.approved}
              </button>
            </div>
          </div>

          {/* Conversation List - Agrupada por telefone */}
          <div className="cvp-conversation-list">
            {loading ? (
              <div className="cvp-loading">
                <Spinner animation="border" size="sm" />
              </div>
            ) : groupedConversations.length === 0 ? (
              <div className="cvp-empty-state" style={{ padding: 20 }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>💬</div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>{t.noConversations}</div>
              </div>
            ) : (
              groupedConversations.map(group => (
                <div
                  key={group.phone_number}
                  className={`cvp-conversation-item ${selectedGroup?.phone_number === group.phone_number ? 'selected' : ''}`}
                  onClick={() => handleSelectGroup(group)}
                >
                  <div
                    className="cvp-avatar"
                    style={{ backgroundColor: getAvatarColor(group.client_name || group.phone_number) }}
                  >
                    {(group.client_name || group.phone_number || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="cvp-conversation-content">
                    <div className="cvp-conversation-header">
                      <span className="cvp-conversation-name">
                        {group.client_name || group.phone_number}
                      </span>
                      <span className="cvp-conversation-time">
                        {formatRelativeDate(group.latest_date, language)}
                      </span>
                    </div>
                    <div className="cvp-conversation-preview">
                      {getPreviewText(group)}
                    </div>
                    <div className="cvp-conversation-meta">
                      {group.all_approved && (
                        <span className="cvp-badge cvp-badge-approved">✓ {t.approvedBadge}</span>
                      )}
                      {group.has_approved && !group.all_approved && (
                        <span className="cvp-badge cvp-badge-pending">Parcial</span>
                      )}
                      <span className="cvp-message-count">
                        {group.total_messages} {t.messages} • {group.conversations.length} sessões
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detail Panel */}
        <div className="cvp-detail-panel">
          {!selectedGroup ? (
            <div className="cvp-empty-state">
              <div className="cvp-empty-icon">💬</div>
              <div className="cvp-empty-title">{t.selectConversation}</div>
              <div className="cvp-empty-subtitle">{t.selectConversationDesc}</div>
            </div>
          ) : loadingDetail ? (
            <div className="cvp-empty-state">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="cvp-detail-header">
                <div className="cvp-detail-client">
                  <div
                    className="cvp-detail-avatar"
                    style={{ backgroundColor: getAvatarColor(selectedGroup.client_name || selectedGroup.phone_number) }}
                  >
                    {(selectedGroup.client_name || selectedGroup.phone_number || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="cvp-detail-info">
                    <h4>{selectedGroup.client_name || selectedGroup.phone_number}</h4>
                    <p>{selectedGroup.phone_number} • {selectedGroup.total_messages} mensagens • {selectedGroup.conversations.length} sessões</p>
                  </div>
                </div>
                <div className="cvp-detail-badges">
                  {selectedGroup.all_approved && (
                    <span className="cvp-badge cvp-badge-approved">✓ {t.approvedBadge}</span>
                  )}
                  {selectedGroup.has_approved && !selectedGroup.all_approved && (
                    <span className="cvp-badge cvp-badge-pending">Parcialmente aprovado</span>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="cvp-messages-area">
                {messageGroups.map((group, groupIndex) => (
                  <div key={groupIndex}>
                    <div className="cvp-date-separator">
                      <span>{formatRelativeDate(group.date, language)}</span>
                    </div>
                    {group.messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`cvp-message ${msg.direction === 'outgoing' ? 'outgoing' : 'incoming'}`}
                      >
                        <div className="cvp-message-bubble">
                          <div className="cvp-message-sender">
                            {msg.direction === 'outgoing' ? t.outgoing : t.incoming}
                          </div>
                          <div className="cvp-message-text">{msg.message_text}</div>
                          <div className="cvp-message-time">
                            {formatTime(msg.timestamp, language)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Action Bar */}
              <div className="cvp-action-bar">
                <textarea
                  className="cvp-notes-input"
                  placeholder={t.notesPlaceholder}
                  value={trainingNotes}
                  onChange={(e) => setTrainingNotes(e.target.value)}
                />
                <div className="cvp-action-buttons">
                  {selectedGroup.all_approved ? (
                    <button
                      className="cvp-btn cvp-btn-remove"
                      onClick={handleRemoveApproval}
                      disabled={actionLoading}
                    >
                      {actionLoading ? <Spinner animation="border" size="sm" /> : '❌'}
                      {t.removeApproval}
                    </button>
                  ) : (
                    <button
                      className="cvp-btn cvp-btn-approve"
                      onClick={handleApprove}
                      disabled={actionLoading}
                    >
                      {actionLoading ? <Spinner animation="border" size="sm" /> : '✅'}
                      {t.approve} ({selectedGroup.conversations.length} sessões)
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
